/**
 * Encounter Library Store
 *
 * Lists available encounter templates, handles deployment (spawning mobs,
 * loading map, arming triggers). Triggers fire on round advancement — see
 * `checkRoundTriggers` which runs whenever encounterStore.round changes.
 */

import { create } from 'zustand';
import type {
  EncounterTemplate,
  EncounterMobSpec,
  EncounterTrigger,
  EncounterHazard,
  MapEnvironment,
  MapMood,
  MapStyle,
} from '@machina/core';
import { trpc } from '../lib/trpc';
import { useMapStore, type MapRequestSource } from './mapStore';
import { useMobStore } from './mobStore';
import { useChatStore } from './chatStore';
import { useIsoSpriteStore, ALL_FACINGS } from './isoSpriteStore';
import { buildMobSpriteParams, type MobSpriteParams } from '../lib/mobSpritePrompt';

// =============================================================================
// TYPES
// =============================================================================

export interface ActiveDeployment {
  templateId: string;
  templateName: string;
  sceneId?: string;
  campaignId: string;
  mapId: string | null;
  spawnedMobIds: string[];
  hazards: EncounterHazard[];
  /** Triggers still awaiting their fire condition */
  pendingTriggers: EncounterTrigger[];
  /** Triggers already fired */
  firedTriggerIds: string[];
  deployedAt: string;
  /** Last round we checked — avoids re-firing on same round */
  lastCheckedRound: number;
  /**
   * Maps mob base-name → EncounterMobSpec, so the iso sprite prompt
   * builder can use the template's imgPrompt hint when generating.
   */
  mobSpecByBaseName: Record<string, EncounterMobSpec>;
}

interface EncounterLibraryState {
  templates: EncounterTemplate[];
  loading: boolean;
  error: string | null;

  /** Currently deployed encounter (only one active at a time) */
  activeDeployment: ActiveDeployment | null;
  deploying: boolean;

  // Actions
  loadTemplates: (campaignId: string) => Promise<void>;
  deployEncounter: (templateId: string, opts: DeployOptions) => Promise<ActiveDeployment | null>;
  checkRoundTriggers: (currentRound: number) => Promise<void>;
  fireTrigger: (triggerId: string) => Promise<void>;
  clearActiveDeployment: () => void;
  deleteTemplate: (id: string) => Promise<void>;
  createTemplate: (payload: CreateTemplatePayload) => Promise<EncounterTemplate | null>;

  // Iso sprite generation for the currently-deployed encounter's mobs
  generateActiveMobSprites: () => Promise<void>;
  regenerateActiveMobSprites: () => Promise<void>;
}

export interface DeployOptions {
  campaignId: string;
  sceneId?: string;
  /** If true, generate fresh map even if template references saved map */
  forceFreshMap?: boolean;
  /** If true, generate fresh token images even if mobs have imgUrl set */
  forceFreshTokens?: boolean;
}

export type CreateTemplatePayload = Parameters<typeof trpc.encounters.create.mutate>[0];

// =============================================================================
// STORE
// =============================================================================

export const useEncounterLibraryStore = create<EncounterLibraryState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,
  activeDeployment: null,
  deploying: false,

  loadTemplates: async (campaignId) => {
    set({ loading: true, error: null });
    try {
      const templates = await trpc.encounters.list.query({ campaignId });
      set({ templates, loading: false });
    } catch (err) {
      console.error('[encounterLibrary] loadTemplates failed', err);
      set({ error: (err as Error).message, loading: false });
    }
  },

  deployEncounter: async (templateId, opts) => {
    if (get().deploying) {
      console.warn('[encounterLibrary] deploy already in progress');
      return null;
    }
    set({ deploying: true });

    try {
      const template = get().templates.find((t) => t.id === templateId)
        ?? (await trpc.encounters.get.query({ id: templateId }));
      if (!template) throw new Error('Template not found');

      // Sweep the board clean before deploy.
      // Iterate TOKENS (not just mobStore.mobs) so we also catch orphan tokens
      // — tokens whose backing mob was already removed but the visual survived
      // from a prior session or botched cleanup. Only clear non-character,
      // non-dead tokens so PC tokens + downed enemies (for continuity) stay.
      const mobStore = useMobStore.getState();
      const { useTokenStore } = await import('./tokenStore');
      const { useCharacterStore } = await import('./characterStore');
      const tokenStore = useTokenStore.getState();
      const characterIds = new Set(Array.from(useCharacterStore.getState().characters.keys()));

      const toClear: string[] = [];
      for (const token of tokenStore.tokens.values()) {
        const cid = token.combatantId;
        if (!cid) continue;
        if (characterIds.has(cid)) continue; // PC — skip
        // Preserve visibly-dead mobs (corpse on-screen). Check mob.dead if we have one.
        const mob = mobStore.mobs.get(cid);
        if (mob?.dead) continue;
        toClear.push(cid);
      }

      if (toClear.length > 0) {
        console.log(`[encounterLibrary] Sweeping ${toClear.length} mob/orphan tokens before deploy`);
        for (const cid of toClear) {
          try { await mobStore.removeMob(cid); } catch { /* may already be gone */ }
          // removeMob only removes the FIRST matching token — kill any extra orphans with the same combatantId.
          for (const [tokenId, token] of tokenStore.tokens) {
            if (token.combatantId === cid) tokenStore.removeToken(tokenId);
          }
        }
      }

      // --- Step 1: load the map -------------------------------------------
      const mapStore = useMapStore.getState();
      const source: MapRequestSource = 'manual-generate';

      let mapId: string | null = null;
      const mapRef = template.map;

      if (mapRef.type === 'saved' && mapRef.savedMapId && !opts.forceFreshMap) {
        await mapStore.loadSavedMapById(mapRef.savedMapId);
        mapId = mapStore.activeMap?.id ?? null;
      } else if (mapRef.type === 'static' && mapRef.staticUrl && !opts.forceFreshMap) {
        const map = await mapStore.requestMap({
          source,
          sceneId: opts.sceneId,
          staticUrl: mapRef.staticUrl,
          name: mapRef.mapName ?? template.name,
          widthFeet: mapRef.widthFeet,
          heightFeet: mapRef.heightFeet,
          environment: mapRef.environment as MapEnvironment | undefined,
          mood: mapRef.mood as MapMood | undefined,
          style: mapRef.style as MapStyle | undefined,
        });
        mapId = map?.id ?? null;
      } else {
        // generate fresh
        const map = await mapStore.requestMap({
          source,
          sceneId: opts.sceneId,
          prompt: mapRef.prompt ?? template.description,
          environment: mapRef.environment as MapEnvironment | undefined,
          mood: mapRef.mood as MapMood | undefined,
          style: mapRef.style as MapStyle | undefined,
          widthFeet: mapRef.widthFeet,
          heightFeet: mapRef.heightFeet,
          name: mapRef.mapName ?? template.name,
          encounterId: template.id,
        });
        mapId = map?.id ?? null;
      }

      // --- Step 2: spawn mobs ---------------------------------------------
      const spawnedMobIds: string[] = [];
      const mobSpecByBaseName: Record<string, EncounterMobSpec> = {};
      for (const mob of template.mobs) {
        const ids = await spawnFromSpec(mob, opts.campaignId, opts.sceneId);
        spawnedMobIds.push(...ids);
        mobSpecByBaseName[mob.name] = mob;
      }

      // --- Step 3: arm triggers -------------------------------------------
      const pendingTriggers = template.triggers.map((t) => ({ ...t, fired: false }));

      // --- Step 4: announce ------------------------------------------------
      if (template.flavorText) {
        useChatStore.getState().addSystemMessage?.(`🎲 ${template.name}\n\n${template.flavorText}`);
      }

      const deployment: ActiveDeployment = {
        templateId: template.id,
        templateName: template.name,
        sceneId: opts.sceneId,
        campaignId: opts.campaignId,
        mapId,
        spawnedMobIds,
        hazards: template.hazards.filter((h) => h.activeAtStart),
        pendingTriggers,
        firedTriggerIds: [],
        deployedAt: new Date().toISOString(),
        lastCheckedRound: 0,
        mobSpecByBaseName,
      };

      set({ activeDeployment: deployment, deploying: false });
      return deployment;
    } catch (err) {
      console.error('[encounterLibrary] deployEncounter failed', err);
      set({ deploying: false, error: (err as Error).message });
      return null;
    }
  },

  checkRoundTriggers: async (currentRound) => {
    const deployment = get().activeDeployment;
    if (!deployment) return;
    if (currentRound === deployment.lastCheckedRound) return;

    const toFire: EncounterTrigger[] = [];
    for (const t of deployment.pendingTriggers) {
      if (t.fired) continue;
      if (t.condition.type === 'round' && currentRound >= t.condition.round) {
        toFire.push(t);
      }
    }

    // Update lastCheckedRound immediately so we don't re-enter
    set({
      activeDeployment: { ...deployment, lastCheckedRound: currentRound },
    });

    for (const t of toFire) {
      await get().fireTrigger(t.id);
    }
  },

  fireTrigger: async (triggerId) => {
    const deployment = get().activeDeployment;
    if (!deployment) return;
    const trigger = deployment.pendingTriggers.find((t) => t.id === triggerId);
    if (!trigger || trigger.fired) return;

    const chat = useChatStore.getState();

    try {
      if (trigger.action.type === 'narration') {
        chat.addSystemMessage?.(`⚡ ${trigger.name}\n\n${trigger.action.text}`);
      } else if (trigger.action.type === 'spawn_mobs') {
        if (trigger.action.narration) {
          chat.addSystemMessage?.(`⚡ ${trigger.name}\n\n${trigger.action.narration}`);
        }
        const newIds: string[] = [];
        const newSpecsByName = { ...deployment.mobSpecByBaseName };
        for (const mob of trigger.action.mobs) {
          const ids = await spawnFromSpec(mob, deployment.campaignId, deployment.sceneId);
          newIds.push(...ids);
          newSpecsByName[mob.name] = mob;
        }
        set({
          activeDeployment: {
            ...deployment,
            spawnedMobIds: [...deployment.spawnedMobIds, ...newIds],
            mobSpecByBaseName: newSpecsByName,
          },
        });
      } else if (trigger.action.type === 'activate_hazard') {
        chat.addSystemMessage?.(`⚡ ${trigger.name}\n\n${trigger.action.narration ?? ''}`);
      } else if (trigger.action.type === 'env_change') {
        chat.addSystemMessage?.(`⚡ ${trigger.name}\n\n${trigger.action.description}`);
      }
    } catch (err) {
      console.error('[encounterLibrary] fireTrigger failed', err);
    }

    // Mark fired
    const updated = get().activeDeployment;
    if (!updated) return;
    set({
      activeDeployment: {
        ...updated,
        pendingTriggers: updated.pendingTriggers.map((t) =>
          t.id === triggerId ? { ...t, fired: true } : t
        ),
        firedTriggerIds: [...updated.firedTriggerIds, triggerId],
      },
    });
  },

  clearActiveDeployment: () => {
    set({ activeDeployment: null });
  },

  deleteTemplate: async (id) => {
    try {
      await trpc.encounters.delete.mutate({ id });
      set((state) => ({ templates: state.templates.filter((t) => t.id !== id) }));
    } catch (err) {
      console.error('[encounterLibrary] deleteTemplate failed', err);
      set({ error: (err as Error).message });
    }
  },

  createTemplate: async (payload) => {
    try {
      const created = await trpc.encounters.create.mutate(payload);
      set((state) => ({ templates: [created, ...state.templates] }));
      return created;
    } catch (err) {
      console.error('[encounterLibrary] createTemplate failed', err);
      set({ error: (err as Error).message });
      return null;
    }
  },

  generateActiveMobSprites: async () => {
    const deployment = get().activeDeployment;
    if (!deployment) return;

    const mobStore = useMobStore.getState();
    const isoStore = useIsoSpriteStore.getState();
    // Per-instance generation — every alive mob gets its own 4-facing set.
    const aliveMobs = deployment.spawnedMobIds
      .map((id) => mobStore.mobs.get(id))
      .filter((m): m is NonNullable<typeof m> => !!m && !m.dead);

    const paramsList: MobSpriteParams[] = aliveMobs.map((mob) => {
      const spec = deployment.mobSpecByBaseName[mob.name] ?? null;
      return buildMobSpriteParams(mob, spec);
    });

    if (paramsList.length === 0) return;
    await isoStore.generateMissing(paramsList);
  },

  regenerateActiveMobSprites: async () => {
    const deployment = get().activeDeployment;
    if (!deployment) return;

    const mobStore = useMobStore.getState();
    const isoStore = useIsoSpriteStore.getState();
    const aliveMobs = deployment.spawnedMobIds
      .map((id) => mobStore.mobs.get(id))
      .filter((m): m is NonNullable<typeof m> => !!m && !m.dead);

    const paramsList: MobSpriteParams[] = aliveMobs.map((mob) => {
      const spec = deployment.mobSpecByBaseName[mob.name] ?? null;
      return buildMobSpriteParams(mob, spec);
    });

    if (paramsList.length === 0) return;

    // Clear existing sprites (per mob UUID) so generateMissing refills fresh.
    useIsoSpriteStore.setState((s) => {
      const newSprites = { ...s.sprites };
      for (const p of paramsList) {
        for (const f of ALL_FACINGS) {
          delete newSprites[`${p.id}:${f}`];
        }
      }
      try { localStorage.setItem('machina:iso-sprites', JSON.stringify(newSprites)); } catch {}
      return { sprites: newSprites };
    });

    await isoStore.generateMissing(paramsList);
  },
}));

// =============================================================================
// HELPERS
// =============================================================================

async function spawnFromSpec(
  mob: EncounterMobSpec,
  campaignId: string,
  sceneId?: string,
): Promise<string[]> {
  const mobStore = useMobStore.getState();
  const ids: string[] = [];

  const result = await mobStore.spawnMob(
    {
      campaignId,
      sceneId,
      bestiaryId: mob.bestiaryId ?? undefined,
      open5eSlug: mob.open5eSlug ?? undefined,
      name: mob.name,
      ac: mob.ac ?? 12,
      maxHp: mob.maxHp ?? 10,
      abilities: mob.abilities,
      actions: mob.actions,
      cr: mob.cr != null ? Number(mob.cr) || undefined : undefined,
      size: mob.size ?? undefined,
      type: mob.creatureType ?? undefined,
      imgUrl: mob.imgUrl ?? undefined,
    },
    {
      count: mob.count ?? 1,
      rollInitiative: true,
      position: typeof mob.placement === 'object' && mob.placement !== null
        ? mob.placement
        : undefined,
    },
  );

  for (const m of result.mobs) {
    ids.push(m.id);
  }
  return ids;
}
